/**
 * Juice (doc 17 §7) — screen shake, hit-stop, and the flash clamp, with the
 * exact numbers the doc names and nothing that is not on that list.
 *
 * "Nothing else shakes" is a rule, not a preference: a game where everything
 * shakes is a game where a shake means nothing. Three causes, three amplitudes.
 *
 * Both shake and hit-stop are switched off entirely by reduced motion (doc 18
 * §3). Neither touches the sim — hit-stop stops the run screen from CONSUMING
 * ticks for 40 ms, and the ticks are all still there afterwards.
 */

/** Doc 17 §7, verbatim. The only three things in the game that shake. */
export const SHAKES = {
  mortar: { px: 1, ms: 90 },
  boss: { px: 2, ms: 180 },
  core: { px: 3, ms: 250 },
} as const;
export type ShakeCause = keyof typeof SHAKES;

/** Doc 17 §7: 40 ms, on mortar impacts and boss damage ≥ 100. Never on a hit. */
export const HIT_STOP_MS = 40;
export const BOSS_HIT_STOP_DAMAGE = 100;

/** Doc 18 §2: Full / Reduced / Off, and what each one multiplies. */
export type FlashChoice = 'full' | 'reduced' | 'off';
const FLASH_SCALE: Record<FlashChoice, number> = { full: 1, reduced: 0.5, off: 0 };

export interface JuiceSettings {
  shake: boolean;
  flash: FlashChoice;
  reducedMotion: boolean;
}

export class Juice {
  private until = 0;
  private amplitude = 0;
  private frozenUntil = 0;
  private settings: JuiceSettings = { shake: true, flash: 'full', reducedMotion: false };

  configure(s: JuiceSettings): void {
    this.settings = s;
    if (s.reducedMotion || !s.shake) this.amplitude = 0;
  }

  /** How bright an additive effect may be, 0..1 (doc 17 §7 / doc 18 §3). */
  get flash(): number {
    return FLASH_SCALE[this.settings.flash];
  }

  shake(cause: ShakeCause, now: number): void {
    if (this.settings.reducedMotion || !this.settings.shake) return;
    const spec = SHAKES[cause];
    // the strongest shake wins rather than stacking: three mortars landing
    // together are one event to the eye, not three times the amplitude
    if (spec.px >= this.amplitude || now >= this.until) {
      this.amplitude = Math.max(this.amplitude, spec.px);
    }
    this.until = Math.max(this.until, now + spec.ms);
  }

  /** Doc 17 §7: a 40 ms freeze. It withholds frames, never ticks. */
  freeze(now: number): void {
    if (this.settings.reducedMotion) return;
    this.frozenUntil = Math.max(this.frozenUntil, now + HIT_STOP_MS);
  }

  frozen(now: number): boolean {
    return now < this.frozenUntil;
  }

  /**
   * Integer pixel offset for this frame, decaying to nothing. Deterministic in
   * `now` so two frames at the same instant agree; it is presentation, but a
   * jitter that disagrees with itself reads as tearing.
   */
  offset(now: number): { x: number; y: number } {
    if (now >= this.until || this.amplitude <= 0) {
      this.amplitude = 0;
      return { x: 0, y: 0 };
    }
    const k = Math.floor(now / 32); // ~30 Hz jitter: pixel-honest, not smooth
    const a = this.amplitude;
    return { x: jitter(k, 1) * a, y: jitter(k, 2) * a };
  }
}

/** −1, 0 or 1 from a cheap integer hash. No wall clock, no allocation. */
function jitter(k: number, salt: number): number {
  const h = Math.imul(k * 2654435761 + salt * 40503, 2246822519) >>> 0;
  return ((h >>> 13) % 3) - 1;
}
